import React, { useEffect, useMemo, useRef, useState } from 'react'
import { StyleSheet, View, Pressable } from 'react-native'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import { BlindScheduleDrawer } from '@/screens/shared/BlindScheduleDrawer'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { formatCountdown } from '../../utils/dateFormatter'
import { useMyTournamentTable } from './useMyTournamentTable'
import { useTournamentPresence } from './useTournamentPresence'

interface TournamentHUDProps {
  tournamentId: Id<'tournaments'>
  userId: string
}

/**
 * Tournament HUD overlay — blinds, next-blind countdown, players remaining,
 * table number, final-table badge, waiting-for-button overlay, and pending /
 * complete table-move banners.
 *
 * Modal flows (reentry, host-bot decision) are NOT owned here — they live in
 * sibling components (`TournamentReentryPrompt`, `TournamentHostBotPrompt`)
 * mounted by `GameContainer` so they survive bust transitions when this
 * HUD unmounts.
 */
export function TournamentHUD({ tournamentId, userId }: TournamentHUDProps) {
  const status = useQuery(api.tournaments.queries.getTournamentStatus, { tournamentId })
  const myTable = useMyTournamentTable(tournamentId)

  useTournamentPresence(tournamentId, userId)

  const [showBlindSchedule, setShowBlindSchedule] = useState(false)
  const [countdown, setCountdown] = useState<string | null>(null)

  // Update countdown timer — capture server value and tick down locally.
  // The snapshot lives in refs (not state) so the tick interval doesn't re-create
  // every time the server pushes a new value. The tick interval writes BOTH the
  // formatted countdown AND the remaining-ms value into state directly so the
  // blind schedule rows can recompute without an additional `useMemo` driven by
  // off-screen `Date.now()` reads.
  const blindSnapshotTimeRef = useRef<number>(0)
  const blindSnapshotValueRef = useRef<number>(0)
  const [remainingUntilNextBlindMs, setRemainingUntilNextBlindMs] = useState<number | null>(null)

  useEffect(() => {
    if (status?.timeUntilNextBlind != null && status.timeUntilNextBlind > 0) {
      blindSnapshotTimeRef.current = Date.now()
      blindSnapshotValueRef.current = status.timeUntilNextBlind
      setRemainingUntilNextBlindMs(status.timeUntilNextBlind)
    } else {
      blindSnapshotValueRef.current = 0
      setRemainingUntilNextBlindMs(null)
    }
  }, [status?.timeUntilNextBlind])

  // One-shot interval effect — created on mount, torn down on unmount. Reads the
  // current snapshot each tick rather than re-subscribing whenever it changes.
  useEffect(() => {
    const update = () => {
      const value = blindSnapshotValueRef.current
      if (value <= 0) {
        setCountdown(null)
        setRemainingUntilNextBlindMs(null)
        return
      }
      const elapsed = Date.now() - blindSnapshotTimeRef.current
      const remaining = Math.max(0, value - elapsed)
      if (remaining <= 0) {
        setCountdown(null)
        setRemainingUntilNextBlindMs(0)
        return
      }
      setCountdown(formatCountdown(remaining))
      setRemainingUntilNextBlindMs(remaining)
    }
    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [])

  // Pre-move warning: when the server stages a `pendingTableMove`, toast the
  // user with the destination table number so they have a heads-up during the
  // remainder of the current hand. Best-effort — if the source table is idle,
  // the orchestrator may stage and apply the move in the same Convex push and
  // the client never observes `pendingTableMove === truthy`. The post-move
  // toast in `useRemovedFromGame` is the guaranteed signal.
  //
  // Dedupe by `destinationGameId` so a re-render of the same staged move
  // (e.g. an unrelated field on the player row updating) doesn't toast twice.
  // A new destinationGameId (consecutive moves) does toast again, which is
  // intentional.
  const announcedMoveDestinationRef = useRef<Id<'games'> | null>(null)
  useEffect(() => {
    const destinationGameId = myTable?.pendingTableMove?.destinationGameId ?? null
    if (!destinationGameId) {
      announcedMoveDestinationRef.current = null
      return
    }
    if (announcedMoveDestinationRef.current === destinationGameId) return
    announcedMoveDestinationRef.current = destinationGameId

    const destTableNumber = myTable?.pendingTableMove?.destinationTableNumber ?? null
    const text = destTableNumber
      ? `Moving to Table ${destTableNumber} after this hand.`
      : 'Moving to another table after this hand.'
    toastNotificationStore.getState().show({ text })
  }, [myTable?.pendingTableMove?.destinationGameId, myTable?.pendingTableMove?.destinationTableNumber])

  // Gate the heavy schedule mapping on `showBlindSchedule`. The drawer is
  // hidden by default — computing every row on every server push (the lobby
  // ticks once per second) is pure waste when nothing's looking at the
  // result. When the drawer IS open the dep array is intentionally narrow:
  // `blindLevels`, `blindLevelIndex`, the interval, and the locally-ticked
  // `remainingUntilNextBlindMs` — not the whole `status` object (which
  // changes reference on every Convex push).
  const blindLevels = status?.blindLevels
  const blindLevelIndex = status?.blindLevelIndex ?? 0
  const blindIncreaseIntervalMs = status?.config.blindIncreaseIntervalMs ?? 0
  const blindScheduleRows = useMemo(() => {
    if (!showBlindSchedule || !blindLevels) return []
    return blindLevels.map((bb, i) => {
      const msUntilLevel = i < blindLevelIndex
        ? null
        : i === blindLevelIndex
          ? 0
          : (remainingUntilNextBlindMs ?? 0) + (i - blindLevelIndex - 1) * blindIncreaseIntervalMs

      const leftLabel = msUntilLevel == null
        ? '--'
        : i === blindLevelIndex
          ? 'Now'
          : msUntilLevel <= 0
            ? 'Next'
            : `${Math.ceil(msUntilLevel / 60_000)}m`

      return {
        bigBlind: bb,
        smallBlind: Math.floor(bb / 2),
        isCurrent: i === blindLevelIndex,
        isPast: i < blindLevelIndex,
        leftLabel
      }
    })
  }, [showBlindSchedule, blindLevels, blindLevelIndex, blindIncreaseIntervalMs, remainingUntilNextBlindMs])

  if (!status) return null

  const isFinalTable = status.activeTableCount === 1

  return (
    <>
      {/* Main HUD bar — centered on table, below winning hand area */}
      <View style={styles.hudWrapper}>
        <View style={styles.hudContainer}>
          <DS.Text variant="Mini/Bold" style={styles.hudText}>
            Blinds: {status.currentSmallBlind}/{status.currentBigBlind}
          </DS.Text>
          {status.nextBlindLevel && countdown && (
            <Pressable onPress={() => setShowBlindSchedule(true)}>
              <DS.Text variant="Mini/Regular" style={[styles.hudSubtext, { fontVariant: ['tabular-nums'] }]}>
                Next: {status.nextBlindLevel.smallBlind}/{status.nextBlindLevel.bigBlind} in {countdown}
              </DS.Text>
            </Pressable>
          )}
          <View style={styles.hudDivider} />
          <View style={styles.hudRow}>
            <DS.Text variant="Mini/Bold" style={styles.hudText}>
              {status.activePlayerCount} left
            </DS.Text>
            {isFinalTable ? (
              <View style={styles.finalTableBadge}>
                <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Yellow'] }}>
                  FINAL TABLE
                </DS.Text>
              </View>
            ) : myTable?.tableNumber ? (
              <DS.Text variant="Mini/Bold" style={styles.hudText}>
                Table {myTable.tableNumber}
              </DS.Text>
            ) : null}
          </View>
        </View>
      </View>

      {/* Waiting-for-button overlay. `pointerEvents="none"` for the same
          reason as `moveBanner`: it's informational, not interactive. */}
      {myTable?.waitingForButton && (
        <View style={styles.waitingOverlay} pointerEvents="none">
          <DS.Text variant="Body Large/Medium" style={{ color: DS.palette['Global/White'], textAlign: 'center' }}>
            You will be dealt in when the Button passes
          </DS.Text>
        </View>
      )}

      {/*
        Pre-move banner. Shows while `pendingTableMove` is staged so the user
        sees the warning in two places simultaneously (toast + inline banner).
        The post-move toast lives in `useRemovedFromGame` and fires across the
        route replace, so no post-move banner is rendered here — the new HUD
        on the destination game has no prior state to compare against.
      */}
      {myTable?.pendingTableMove && (
        // `pointerEvents="none"` so this purely informational banner can't
        // intercept taps on the bottom action controls beneath it. The banner
        // sits at `bottom: 0` and spans the full width, so without this the
        // user couldn't fold/call/raise while a move was staged.
        <View style={styles.moveBanner} pointerEvents="none">
          <DS.Text variant="Body Small/Medium" style={{ color: DS.palette['Global/White'], textAlign: 'center' }}>
            {myTable.pendingTableMove.destinationTableNumber
              ? `Moving to Table ${myTable.pendingTableMove.destinationTableNumber} after this hand`
              : 'Moving to another table after this hand'}
          </DS.Text>
        </View>
      )}

      <BlindScheduleDrawer
        visible={showBlindSchedule}
        onClose={() => setShowBlindSchedule(false)}
        rows={blindScheduleRows}
      />
    </>
  )
}

const styles = StyleSheet.create({
  hudWrapper: {
    position: 'absolute',
    bottom: 270,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 50,
    pointerEvents: 'box-none'
  },
  hudContainer: {
    alignItems: 'center',
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.25),
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    minWidth: 150,
    gap: 2
  },
  hudRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  hudDivider: {
    height: 1,
    alignSelf: 'stretch',
    backgroundColor: getDSColorWithOpacity('Global/White', 0.2)
  },
  finalTableBadge: {
    backgroundColor: getDSColorWithOpacity('Brand/Yellow', 0.2),
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2
  },
  hudText: {
    color: DS.palette['Global/White']
  },
  hudSubtext: {
    color: DS.palette['Brand/Light Grey']
  },
  waitingOverlay: {
    position: 'absolute',
    bottom: 120,
    left: 24,
    right: 24,
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.8),
    borderRadius: 12,
    padding: 16,
    zIndex: 99
  },
  moveBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: getDSColorWithOpacity('Brand/Maroon', 0.9),
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99
  }
})
